(() => {
  const RAMP_DURATION = 500;
  const ANIMATION_NAME = "marquee-motion";
  // Hover "stops" the marquee by ramping to a rate this small (~1px per hour)
  // rather than to 0 or via pause(). Both of those hand the animation to engine
  // paths that misbehave: WebKit re-anchors a rate-0 animation to its first
  // frame, and pause()/play() tears the accelerated animation down and back up.
  // Staying running at a microscopic rate keeps one code path in every engine.
  const STOPPED_RATE = 0.001;
  // Viewport the speed scale is tuned against, and how much of the narrow-screen
  // slowdown to cancel — see #narrowFactor.
  const REFERENCE_WIDTH = 1440;
  const NARROW_COMPENSATION = 0.5;
  // smoothstep — ease-in-out so the speed change has no hard edges
  const easing = (t) => t * t * (3 - 2 * t);

  // Animate a value from -> to over duration and expose the live value + a cancel handle.
  function animateValue({ from, to, duration, onUpdate, onComplete }) {
    const start = performance.now();
    let current = from;
    let cancelled = false;

    const frame = (now) => {
      if (cancelled) return;
      const progress = Math.min((now - start) / duration, 1);
      current = from + (to - from) * easing(progress);
      onUpdate(current);
      if (progress < 1) requestAnimationFrame(frame);
      else if (onComplete) onComplete();
    };
    requestAnimationFrame(frame);

    return {
      get current() {
        return current;
      },
      cancel() {
        cancelled = true;
      },
    };
  }

  function debounce(fn, wait) {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }

  class MarqueeTicker extends HTMLElement {
    #wrapper = null;
    #content = null;
    #items = null;
    #template = null; // pristine single item, captured once, survives re-connects
    #baseDirection = null; // direction the setting asks for; RTL mirrors it
    #generation = 0; // invalidates in-flight async builds when the element re-connects
    #hoverTarget = null;
    #ramp = null; // playbackRate ramp handle
    #stopHolds = new Set(); // reasons currently holding the row still (hover, focus)
    #containerWidth = null;
    #itemWidth = null;
    #itemsObserver = null;

    async connectedCallback() {
      this.#wrapper = this.querySelector(".marquee__wrapper");
      this.#content = this.querySelector(".marquee__content");
      if (!this.#wrapper || !this.#content) return;

      if (!this.#template) {
        const firstItem = this.#content.querySelector(".marquee__items");
        if (!firstItem) return;
        this.#template = firstItem.cloneNode(true);
      }

      if (this.#baseDirection === null) {
        this.#baseDirection =
          this.style.getPropertyValue("--marquee-direction").trim() || "normal";
      }
      const isRtlPage = document.documentElement.dir === "rtl";
      this.style.setProperty(
        "--marquee-direction",
        isRtlPage
          ? this.#baseDirection === "reverse"
            ? "normal"
            : "reverse"
          : this.#baseDirection
      );

      const generation = ++this.#generation;
      this.#reset();

      // Fonts change item widths, which changes how many copies fill the row —
      // measure only once they are ready to avoid a re-fill/jump on swap.
      if (document.fonts && document.fonts.status !== "loaded") {
        try {
          await document.fonts.ready;
        } catch (e) {
          // ignore
        }
      }
      if (!this.isConnected || generation !== this.#generation) return;

      const { copies } = this.#queryCopies();

      this.#addCopies(copies - 1);
      this.#duplicateContent();
      this.#setSpeed(copies);

      window.addEventListener("resize", this.#onResize);

      // A section added in the Theme Editor injects its stylesheet alongside its
      // markup, so the first measurement can land before the slide widths exist —
      // that under-measures the item and overfills the row. Re-measure whenever
      // the item actually resizes (late CSS, font swap, image load).
      this.#itemsObserver = new ResizeObserver(this.#onItemsResize);
      this.#itemsObserver.observe(this.#items);

      if (
        this.dataset.stopOnHover === "true" &&
        window.matchMedia("(hover: hover)").matches
      ) {
        this.#hoverTarget =
          (this.dataset.pauseTarget &&
            this.closest(this.dataset.pauseTarget)) ||
          this;
        this.#hoverTarget.addEventListener(
          "pointerenter",
          this.#onPointerEnter
        );
        this.#hoverTarget.addEventListener(
          "pointerleave",
          this.#onPointerLeave
        );
      }

      // Keyboard focus holds the row too, whatever the hover setting says: a link
      // that keeps moving away can't be read or activated without a pointer.
      this.addEventListener("focusin", this.#onFocusIn);
      this.addEventListener("focusout", this.#onFocusOut);
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.#onResize);
      this.#onResize.cancel();
      this.#itemsObserver?.disconnect();
      this.#itemsObserver = null;
      if (this.#hoverTarget) {
        this.#hoverTarget.removeEventListener(
          "pointerenter",
          this.#onPointerEnter
        );
        this.#hoverTarget.removeEventListener(
          "pointerleave",
          this.#onPointerLeave
        );
        this.#hoverTarget = null;
      }
      this.removeEventListener("focusin", this.#onFocusIn);
      this.removeEventListener("focusout", this.#onFocusOut);
      this.#stopHolds.clear();
      this.#ramp?.cancel();
      this.#ramp = null;
    }

    #reset() {
      this.#ramp?.cancel();
      this.#ramp = null;
      this.#stopHolds.clear();
      // A re-connect (Theme Editor) reuses the wrapper, so a hover ramp left
      // over from the previous session would freeze the fresh marquee.
      const animation = this.#animation;
      if (animation) animation.playbackRate = 1;
      this.#contentClone?.remove();
      this.#content.replaceChildren(this.#template.cloneNode(true));
      this.#items = this.#content.firstElementChild;
      this.style.removeProperty("--marquee-speed");
    }

    get #animation() {
      const animations = this.#wrapper?.getAnimations() ?? [];
      // A CSS transition on the wrapper can sort ahead of the marquee keyframes.
      return (
        animations.find((item) => item.animationName === ANIMATION_NAME) ??
        animations[0] ??
        null
      );
    }

    #onPointerEnter = () => this.#hold("hover");
    #onPointerLeave = () => this.#release("hover");

    // Keyboard focus only: a click or tap on a card link focuses it too, and
    // stopping the row on every tap (or on a focus the back button restores)
    // would read as the ticker freezing on its own.
    #onFocusIn = (event) => {
      if (!event.target?.matches?.(":focus-visible")) return;
      this.#hold("focus");
    };

    #onFocusOut = (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      // Focusing a slide that sits outside the visible row scrolls this
      // overflow container; reset it so the row keeps its position.
      this.scrollLeft = 0;
      this.#release("focus");
    };

    #hold(reason) {
      this.#stopHolds.add(reason);
      this.#rampTo(STOPPED_RATE);
    }

    #release(reason) {
      if (!this.#stopHolds.delete(reason)) return;
      if (this.#stopHolds.size === 0) this.#rampTo(1);
    }

    #rampTo(target) {
      const animation = this.#animation;
      if (!animation) return; // reduced-motion: no animation to control

      const from = this.#ramp?.current ?? animation.playbackRate;
      this.#ramp?.cancel();
      this.#ramp = animateValue({
        from,
        to: target,
        duration: RAMP_DURATION,
        // Assign playbackRate directly — the setter re-seeks currentTime, so the
        // marquee keeps its position. updatePlaybackRate() instead defers the
        // change to an async task that WebKit re-anchors from the wrong time,
        // snapping the track backwards on hover.
        onUpdate: (value) => {
          animation.playbackRate = value;
        },
        onComplete: () => {
          this.#ramp = null;
        },
      });
    }

    #setSpeed(copies) {
      // ticker_speed 1..30 (30 = fastest) maps to a ~70s…24s loop; more copies =>
      // longer loop, so sqrt keeps the perceived speed close across widths.
      // data-normalize-overflow (opt-in, used by hero): an item set WIDER than
      // the container keeps copies at 1, so scale by the overflow — px/s then
      // stays constant however many slides the set holds. Off by default.
      const speed = Number(this.dataset.speed) || 15;
      const speedFactor = (45 - speed) * 1.6;
      let overflow = 1;
      if (
        this.dataset.normalizeOverflow === "true" &&
        this.#containerWidth > 0 &&
        this.#itemWidth > this.#containerWidth
      ) {
        overflow = this.#itemWidth / this.#containerWidth;
      }
      const duration =
        Math.sqrt(copies) * speedFactor * overflow * this.#narrowFactor();
      this.style.setProperty("--marquee-speed", `${duration}s`);
    }

    // The scale above paces the row in screens-per-second, which is identical on
    // every viewport — but a phone shows far fewer slides per screen, so the same
    // pacing reads as barely moving. Shorten the loop on narrow viewports by part
    // of that gap (0.5 = halfway, 1 would match desktop px/s). Reference width and
    // wider are untouched.
    #narrowFactor() {
      const width = this.#containerWidth;
      if (!width || width >= REFERENCE_WIDTH) return 1;
      return Math.pow(width / REFERENCE_WIDTH, NARROW_COMPENSATION);
    }

    #queryCopies() {
      const rootWidth = this.getBoundingClientRect().width;
      const itemWidth = this.#items
        ? this.#items.getBoundingClientRect().width
        : 0;
      const widthChanged = this.#containerWidth !== rootWidth;
      const itemChanged = this.#itemWidth !== itemWidth;
      this.#containerWidth = rootWidth;
      this.#itemWidth = itemWidth;

      return {
        copies: itemWidth === 0 ? 1 : Math.ceil(rootWidth / itemWidth),
        widthChanged,
        itemChanged,
      };
    }

    #addCopies(count) {
      if (!this.#items || count < 1) return;
      for (let i = 0; i < count; i++) {
        this.#content.appendChild(this.#cloneHidden(this.#items));
      }
    }

    // Copies are aria-hidden duplicates, so their links/buttons must leave the
    // tab order — otherwise keyboard focus lands on content screen readers skip.
    #cloneHidden(node) {
      const clone = node.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone
        .querySelectorAll(
          "a[href], button, input, select, textarea, [tabindex]"
        )
        .forEach((item) => item.setAttribute("tabindex", "-1"));
      return clone;
    }

    #removeCopies(count) {
      // Always keep at least one item block in the content.
      const removable = Math.min(count, this.#content.children.length - 1);
      for (let i = 0; i < removable; i++) {
        this.#content.lastElementChild?.remove();
      }
    }

    get #contentClone() {
      const last = this.#wrapper.lastElementChild;
      return this.#content !== last ? last : null;
    }

    // The seamless half: a full copy of the content trailing the original.
    #duplicateContent() {
      this.#contentClone?.remove();
      this.#wrapper.appendChild(this.#cloneHidden(this.#content));
    }

    #onResize = debounce(() => this.#syncLayout(), 250);

    // Item size changes are discrete (late editor CSS, font swap, image load)
    // and can land right after the animation starts — apply them in the same
    // frame, not 250ms later, so the restart happens before the row visibly
    // moves and never reads as a speed change + snap back.
    #onItemsResize = () => {
      this.#onResize.cancel();
      this.#syncLayout();
    };

    #syncLayout() {
      if (!this.isConnected || !this.#items) return;

      const { copies, widthChanged, itemChanged } = this.#queryCopies();
      if (!widthChanged && !itemChanged) return;

      const current = this.#content.children.length;
      if (copies > current) this.#addCopies(copies - current);
      else if (copies < current) this.#removeCopies(current - copies);

      this.#duplicateContent();
      this.#setSpeed(copies);
      this.#restartAnimation();
    }

    #restartAnimation() {
      this.#ramp?.cancel();
      this.#ramp = null;
      const animations = this.#wrapper.getAnimations();
      const isStopped = this.#stopHolds.size > 0;
      requestAnimationFrame(() => {
        for (const animation of animations) {
          animation.currentTime = 0;
          if (!isStopped) animation.playbackRate = 1;
        }
      });
    }
  }

  if (!customElements.get("marquee-ticker")) {
    customElements.define("marquee-ticker", MarqueeTicker);
  }
})();
